import React, { useState } from 'react';
import { Button, Input, message, Result, Space, Table } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ApiError } from '../../api/types';
import { formatDateTime } from '../../utils/format';
import { GlobalConfigEntry } from './types';
import { useGlobalConfig, useReloadGlobalConfig } from './queries';

const errorText = (error: unknown) => error instanceof ApiError
  ? `${error.code ? `[${error.code}] ` : ''}${error.message}`
  : error instanceof Error ? error.message : 'Request failed. Please try again.';

export const GlobalConfigPage: React.FC = () => {
  const configs = useGlobalConfig();
  const reload = useReloadGlobalConfig();
  const [messageApi, contextHolder] = message.useMessage();

  const handleReload = async () => {
    try {
      await reload.mutateAsync();
      messageApi.success('Configuration reloaded. Other servers will update shortly.');
    } catch (error) {
      messageApi.error(`Reload failed; caches may be partially cleared. ${errorText(error)}`);
    }
  };

  const [search, setSearch] = useState('');
  if (configs.error) return <Result status="error" title="Failed to load global configuration" subTitle={errorText(configs.error)} extra={<Button onClick={() => configs.refetch()}>Retry</Button>} />;
  const term = search.trim().toLowerCase();
  const rows = (configs.data || []).filter(row => [row.key, row.value, row.description || ''].some(value => value.toLowerCase().includes(term)));
  const columns: ColumnsType<GlobalConfigEntry> = [
    { title: 'Key', dataIndex: 'key', width: 320, render: value => <span style={{ overflowWrap: 'anywhere' }}>{value}</span> },
    { title: 'Value', dataIndex: 'value', width: 220, render: value => <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{value}</span> },
    { title: 'Description', dataIndex: 'description', render: value => value || '—' },
    { title: 'Updated', dataIndex: 'updated_at', width: 180, render: formatDateTime },
  ];
  return <>
    {contextHolder}
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      <Input.Search aria-label="Search global configuration" placeholder="Search key, value, or description" allowClear value={search} onChange={event => setSearch(event.target.value)} style={{ maxWidth: 360 }} />
      <Space>
        <Button icon={<ReloadOutlined />} loading={configs.isFetching} onClick={() => configs.refetch()}>Refresh</Button>
        <Button type="primary" loading={reload.isPending} onClick={handleReload}>Reload config &amp; clear caches</Button>
      </Space>
    </div>
    <Table rowKey="key" columns={columns} dataSource={rows} loading={configs.isLoading} pagination={false} scroll={{ x: 1100 }} />
  </>;
};
